/**
 * 🖐️ **The thing in your hand, built as GEOMETRY from the pack's own drawing.**
 *
 * 🎯 **haugur, 2026-09-02:** *"pics of shovel and axe and pick and blocks in
 * hand, still looks funky."* **He is right, and it was the third attempt that
 * finally had somewhere to go.**
 *
 * ## The three attempts, because the reasoning matters more than the result
 *
 * | | |
 * |---|---|
 * | ① **Boxes** | A shaft and a head. **All three tools were the same object** — a mallet — and only the tier changed its colour |
 * | ② **A texture** | The pack's real picture on a quad. ✅ It looked right. 🔴 **It turned dev and staging completely black**, because a texture is a new shader permutation and **a shader cannot be checked on this workstation at all** |
 * | ③ **This** | The pack's real picture as **triangles** |
 *
 * 🔴 **`SVGLoader.createShapes()` turns our icon paths into filled shapes**, and
 * a shape is `ShapeGeometry` — ordinary triangles wearing the
 * `MeshBasicMaterial` the running game already compiles. **Nothing new has to
 * compile, and the picture is the one haugur approved.**
 *
 * ⚠️ **The safety argument is the point, not the cleverness.** A shader failure
 * happens inside the renderer and takes the frame with it — **it cannot be
 * caught.** A parse failure here is ordinary JavaScript: {@link heldItemObject}
 * wraps it, returns `null`, and the hand falls back to a bare sleeve. **The
 * worst case is the hand haugur already has, not a black screen.**
 *
 * ## ⚠️ A block is a CUBE, and that is not a shortcut
 *
 * A block's pack icon is an `<image>` of its real tile, and **SVGLoader cannot
 * make shapes out of a bitmap.** So a held block is built as an actual cube in
 * the block's own researched colours — which is what `itCube` draws in the pack
 * anyway, and what Minecraft does: *the picture of a thing you can place should
 * be the thing.*
 */

import {
  BoxGeometry,
  Color,
  DataTexture,
  DoubleSide,
  Group,
  LinearFilter,
  Mesh,
  NearestFilter,
  MeshBasicMaterial,
  PlaneGeometry,
  RGBAFormat,
  ShapeGeometry,
  SRGBColorSpace,
  TextureLoader,
  type Material,
  type Texture,
} from 'three';
import { MeshBasicNodeMaterial } from 'three/webgpu';
import { texture as textureNode, uniform, uv, vec3 } from 'three/tsl';
import { SVGLoader } from 'three/examples/jsm/loaders/SVGLoader.js';
import {
  blockAppearance,
  blockIdForKey,
  DEFAULT_TILE,
  itemById,
  itemIconUri,
  itemIdForKey,
  itemsWithIcons,
  renderTile,
  tilePlans,
  type FaceKind,
} from '@lofi/shared';
import { makeParts, type SlotContent } from './hud-parts.ts';

/* The parts module needs a slot geometry; only `picture` is used and it reads
   neither number. */
const P = makeParts({ SLOT: 48, GAP: 4 });

/** The grid the icons are drawn on. */
const ICON = 32;

/**
 * How big the held item is, in metres.
 *
 * 🎯 **haugur sent a photograph of Minecraft to correct the FIRST version's
 * placement** — *"should be at bottom and to right"* — and this is the size that
 * goes with that corner. **One number if it wants changing.**
 */
export const HELD_SIZE = 0.46;

/**
 * ⚠️ **How far apart stacked paths sit, in icon units.**
 *
 * The icon's paths are drawn one on top of another — a blade, then its lit
 * facet, then its shadow — and **flat shapes at the same depth z-fight**, which
 * looks like the tool flickering apart as you move. A hair of separation per
 * path fixes it and is far too small to see edge-on.
 */
const LAYER = 0.05;

/** Everything a held object needs to be dimmed at night. */
export interface HeldObject {
  readonly object: Group;
  /** Each material, with the colour it was authored at. */
  readonly lit: readonly { readonly material: MeshBasicMaterial; readonly base: Color }[];
  /**
   * 🌙 **How a photographed item is dimmed**, when `lit` cannot do it.
   *
   * ⚠️ **A node material IGNORES `material.color`** once it has a `colorNode`,
   * so the photograph would have stayed noon-bright at three in the morning —
   * the exact fault T150 piece ④ fixed for the sleeve. This multiplies the
   * light into the colour node instead.
   */
  readonly dim?: ((level: number) => void) | undefined;
}

const cache = new Map<string, HeldObject | null>();

function keyOf(content: SlotContent): string {
  return `${content.item ?? ''}|${content.colour ?? ''}|${content.iconUri === undefined ? '' : 'i'}`;
}

function basic(colour: Color | number): MeshBasicMaterial {
  return new MeshBasicMaterial({
    color: colour,
    /* The same flags the sleeve and the old break-box use, and for the same
       reason: the hand sits centimetres from the eye and must never be
       swallowed by a block the player is standing against. */
    depthTest: false,
    depthWrite: false,
    /* ⚠️ A drawing has a front and a back, and it is turned toward the player
       either way once the swing rotates it. */
    side: 2,
  });
}

/**
 * 🪓 **The photographs, decoded once and kept.**
 *
 * 🔴 **Nothing is built from one of these until its image has DECODED.** A
 * texture whose source is still empty would reach the renderer half-made, and
 * the whole point of this file is that the hand never takes the frame with it.
 * Until a photograph is ready the drawn triangles are used, which is exactly
 * what the hand did before.
 */
const photos = new Map<string, Texture>();

/**
 * 🖐️ **Decode the tool photographs, so the hand can wear them.**
 *
 * Called once at start-up. There are six, they are `data:` URIs already in the
 * bundle, and nothing waits on them: a hand asked for before they land gets the
 * drawing.
 */
export async function preloadHeldPhotos(): Promise<void> {
  const loader = new TextureLoader();
  const keys = itemsWithIcons();
  console.info(`[held] decoding ${String(keys.length)} tool photograph(s)`);
  await Promise.all(
    keys.map(async (key) => {
      const uri = itemIconUri(key);
      if (uri === undefined) return;
      try {
        const map = await loader.loadAsync(uri);
        map.colorSpace = SRGBColorSpace;
        /* ⚠️ Linear, not nearest. This is a photograph scaled down to half a
           metre, not a 64 px tile — the same call `hud-parts.ts` makes when it
           deliberately leaves `image-rendering` alone for these. */
        map.magFilter = LinearFilter;
        map.minFilter = LinearFilter;
        map.generateMipmaps = false;
        photos.set(key, map);
        /* A photograph arriving invalidates the drawing that was cached for the
           same item, or the hand would keep handing back triangles for ever. */
        for (const k of [...cache.keys()]) if (k.startsWith(`${key}|`)) cache.delete(k);
      } catch (err: unknown) {
        /**
         * 🔴 A photograph that will not decode is a DRAWN tool, not a bare hand
         * and never a dead frame — **but it must SAY SO.**
         *
         * ⚠️ **This catch was silent for one deploy and cost two rounds of
         * guessing.** `shoot.mjs` reads the console, so a loud failure here is
         * the difference between a diagnosis and a third guess.
         */
        console.error(`[held] the photograph for "${key}" did not decode`, err);
      }
    }),
  );
  console.info(`[held] ${String(photos.size)} photograph(s) ready for the hand`);
}

/**
 * 📷 **The photograph on a quad — attempt ② done again, on purpose.**
 *
 * 🎯 **haugur, 2026-09-04:** *"the new images for axe and shovel etc etc, they
 * have just been put in as images on menus, not in the hud itself, should we get
 * that done."* **The pack and the hand had been two different pictures of the
 * same axe since 2026-09-02.**
 *
 * ## 🔴 Why this is not the change that blacked out both environments
 *
 * **It was measured, not remembered.** The reverted commit `d55ff8b` did this:
 *
 * ```
 * new MeshBasicMaterial({ … })        // a PLAIN material, auto-translated
 * material.map = texture              // …then MUTATED, mid-session
 * material.needsUpdate = true         // …forcing a pipeline rebuild
 * ```
 *
 * ⚠️ **A plain `MeshBasicMaterial` under `three/webgpu` is converted to a node
 * material behind your back, and giving it a `map` afterwards re-derives that
 * graph while the renderer is running.** That is a new shader permutation
 * compiled mid-frame, and a shader failure cannot be caught.
 *
 * ✅ **This builds the node graph UP FRONT on a material the running game
 * already compiles.** `flame.ts` has shipped exactly this shape since
 * 2026-08-31 — `MeshBasicNodeMaterial`, a `texture(map, uv())` colour node, an
 * opacity node off the same sample — and `burmesh.ts` puts a photographed door
 * on screen the same way. **Both are on screen in haugur's own screenshot.**
 *
 * 🔴 **The residual risk is still real and is NOT nothing**, because
 * `navigator.gpu` does not exist on this workstation and `shoot.mjs` runs
 * WebGL2, which never reproduced the fault. **Only haugur's eye can pass this.**
 * ✅ **A rollback is the DEPLOY and the COMMIT, both.**
 */
function fromPhoto(map: Texture): HeldObject {
  const material = new MeshBasicNodeMaterial();
  const tile = textureNode(map, uv());
  /* 🌙 The light the player is standing in, multiplied into the picture. */
  const light = uniform(1);
  material.colorNode = vec3(tile.r, tile.g, tile.b).mul(light);
  material.opacityNode = tile.a;
  material.transparent = true;
  /* Anything this faint is the transparent surround of the photograph. */
  material.alphaTest = 0.12;
  /* The same three flags the drawn tool and the sleeve use: the hand sits
     centimetres from the eye and must never be swallowed by a block the player
     is standing against. */
  material.depthTest = false;
  material.depthWrite = false;
  material.side = DoubleSide;

  const mesh = new Mesh(new PlaneGeometry(HELD_SIZE, HELD_SIZE), material);
  mesh.renderOrder = 1002;

  const object = new Group();
  object.add(mesh);
  return {
    object,
    lit: [],
    dim: (level: number) => {
      light.value = level;
    },
  };
}

/**
 * 🪓 Build one icon as filled triangles.
 *
 * ⚠️ **SVG's Y axis points DOWN and three's points UP**, so the whole thing is
 * flipped. Forgetting that draws every tool upside down, which reads as a
 * modelling mistake rather than as a sign error.
 */
function fromIcon(svgMarkup: string): HeldObject | null {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${ICON} ${ICON}">${svgMarkup}</svg>`;
  const parsed = new SVGLoader().parse(svg);
  if (parsed.paths.length === 0) return null;

  const inner = new Group();
  const lit: { material: MeshBasicMaterial; base: Color }[] = [];
  parsed.paths.forEach((path, index) => {
    const material = basic(path.color);
    lit.push({ material, base: path.color.clone() });
    for (const shape of SVGLoader.createShapes(path)) {
      const mesh = new Mesh(new ShapeGeometry(shape), material);
      mesh.position.z = index * LAYER;
      mesh.renderOrder = 1002 + index;
      inner.add(mesh);
    }
  });
  if (inner.children.length === 0) return null;

  /* Centre the 32-unit drawing on the origin, then flip Y and scale to metres. */
  inner.position.set(-ICON / 2, -ICON / 2, 0);
  const object = new Group();
  const s = HELD_SIZE / ICON;
  object.scale.set(s, -s, s);
  object.add(inner);
  return { object, lit };
}

/**
 * 🧱 **The tile the game actually draws for one face of one block**, as a
 * texture, or `undefined` where the block has no plan for that face.
 *
 * ⚠️ **The same call `blockicons.ts` makes for the PACK icon**, deliberately —
 * so the cube in your hand and the picture in your pack cannot come to disagree,
 * and regenerating a texture improves both for free.
 */
function tileTexture(blockKey: string, face: FaceKind): DataTexture | undefined {
  const id = blockIdForKey(blockKey);
  if (id === undefined) return undefined;
  const plan = tilePlans().find((p) => p.block === id && p.face === face);
  if (plan === undefined) return undefined;
  const rgba = renderTile(plan, DEFAULT_TILE);
  const tex = new DataTexture(rgba, DEFAULT_TILE, DEFAULT_TILE, RGBAFormat);
  tex.colorSpace = SRGBColorSpace;
  /* 🔴 Nearest, unlike the tool photographs. **A block tile IS pixels** — the
     world's own mesher shows them crisp, and a held cube that softened them
     would be the one blurry block in the game. */
  tex.magFilter = NearestFilter;
  tex.minFilter = NearestFilter;
  tex.generateMipmaps = false;
  tex.needsUpdate = true;
  return tex;
}

/**
 * 🧱 **One face of a held cube, wearing the block's real picture.**
 *
 * @param shade how much darker than the top this face is, 0…1
 */
function faceMaterial(tex: DataTexture, shade: number, light: ReturnType<typeof uniform>) {
  const material = new MeshBasicNodeMaterial();
  const tile = textureNode(tex, uv());
  /* 🎨 **Shading the FACE, not the texture** — a cube reads as one object
     catching light because its faces differ in value while sharing a picture.
     The same rule `blockicons.ts` follows for the pack, and Minecraft's own. */
  material.colorNode = vec3(tile.r, tile.g, tile.b)
    .mul(1 - shade)
    .mul(light);
  material.depthTest = false;
  material.depthWrite = false;
  /**
   * 🔴 **The shade, written where a test can read it.**
   *
   * ⚠️ **A node material IGNORES `material.color`**, so the value that used to
   * carry this — and that `hand.test.ts` used to assert on — is now a white
   * default on all six faces. **The property still holds and must stay
   * checkable: a cube reads as a cube because its faces differ in value.**
   */
  material.userData['shade'] = shade;
  return material;
}

/**
 * 🧱 **A held block, faced with the tiles the world draws.**
 *
 * 🎯 **haugur, 2026-09-04**, holding brushwood beside the brushwood on the
 * ground: the cube in hand was a **plain flat colour** while the block behind it
 * had a real picture. **The tools became photographs on 2026-09-02 and this did
 * not, so the same fault the photographs fixed had simply moved.**
 *
 * 🔴 **Why this is safe.** `MeshBasicNodeMaterial` fed a `DataTexture` through a
 * `texture()` colour node is exactly what `rain.ts` and `smoke.ts` have shipped
 * since 2026-08-31 — **built up front, never mutated afterwards**, which is the
 * difference between this and the material that blacked out both environments.
 *
 * ⚠️ **The flat colours are kept as the fallback** for a block with no tile
 * plan, which is what every block looked like before this.
 */
function fromBlockTiles(blockKey: string): HeldObject | null {
  const top = tileTexture(blockKey, 'top');
  const side = tileTexture(blockKey, 'side');
  if (top === undefined || side === undefined) return null;

  const light = uniform(1);
  /* `BoxGeometry` groups its faces in the order +x, −x, +y, −y, +z, −z. */
  const materials: Material[] = [
    faceMaterial(side, 0.22, light),
    faceMaterial(side, 0.42, light),
    faceMaterial(top, 0, light),
    faceMaterial(side, 0.42, light),
    faceMaterial(side, 0.22, light),
    faceMaterial(side, 0.42, light),
  ];

  const size = HELD_SIZE * 0.62;
  const mesh = new Mesh(new BoxGeometry(size, size, size), materials);
  mesh.renderOrder = 1002;
  /* Turned so three faces show, which is what makes a cube read as a cube. */
  mesh.rotation.set(0.42, 0.72, 0);

  const object = new Group();
  object.add(mesh);
  return {
    object,
    lit: [],
    dim: (level: number) => {
      light.value = level;
    },
  };
}

/**
 * 🧱 Build a held block as an actual cube in its own colours.
 *
 * 🎨 **Two values from one colour** — the rule the pack icons follow: one
 * material catching light, never three colours chosen side by side.
 *
 * ⚠️ **The FALLBACK since 2026-09-04**, for a block with no tile plan.
 * {@link fromBlockTiles} is tried first and wears the real picture.
 */
function fromBlock(blockKey: string): HeldObject | null {
  const look = blockAppearance(blockIdForKey(blockKey) ?? -1);
  if (look === undefined) return null;

  const top = new Color(look.top).lerp(new Color(0xffffff), 0.24);
  const side = new Color(look.side);
  const dark = side.clone().lerp(new Color(0x000000), 0.34);

  /* One geometry, six faces, three materials — `BoxGeometry` groups its faces
     in the order +x, −x, +y, −y, +z, −z. */
  const materials: Material[] = [
    basic(side),
    basic(dark),
    basic(top),
    basic(dark),
    basic(side),
    basic(dark),
  ];
  const lit = materials.map((m) => ({
    material: m as MeshBasicMaterial,
    base: (m as MeshBasicMaterial).color.clone(),
  }));

  const size = HELD_SIZE * 0.62;
  const mesh = new Mesh(new BoxGeometry(size, size, size), materials);
  mesh.renderOrder = 1002;
  /* Turned so three faces show, which is what makes a cube read as a cube. */
  mesh.rotation.set(0.42, 0.72, 0);

  const object = new Group();
  object.add(mesh);
  return { object, lit };
}

/**
 * 🖐️ **What to put in the hand for this slot**, or `null` for a bare hand.
 *
 * 🔴 **Every failure returns `null` rather than throwing.** The hand is what you
 * hold everything else *with*, so it is the right fallback — and an item whose
 * drawing cannot be built must not take the renderer down with it. **That is the
 * whole reason this is geometry and not a texture.**
 */
export function heldItemObject(content: SlotContent): HeldObject | null {
  if (content.item === undefined) return null;
  const key = keyOf(content);
  const had = cache.get(key);
  if (had !== undefined) return had;

  let built: HeldObject | null = null;
  try {
    /**
     * 📷 **The photograph first, when it has decoded** — so the axe in your hand
     * is the axe in your pack. 🎯 haugur, 2026-09-04: *"they have just been put
     * in as images on menus, not in the hud itself."*
     *
     * ⚠️ **Before it decodes this falls through to the drawing**, which is the
     * hand the game has had all along. Nothing waits, and nothing is empty.
     */
    const photo = photos.get(content.item);
    if (photo !== undefined) {
      built = fromPhoto(photo);
      cache.set(key, built);
      return built;
    }
    /**
     * ⚠️ **Asked of `ITEMS` directly, not guessed from the markup.**
     *
     * An earlier version sniffed the string `picture()` returned and treated
     * anything that was not an `<image>` as a drawing. 🔴 **That was wrong for a
     * block with no generated tile**, whose picture is a *drawn* cube — it would
     * have been sent down the SVG path and come back as a bare hand.
     *
     * 👉 **`ITEMS` is the same list `picture()` asks first**, so this agrees with
     * the pack by construction rather than by inspection.
     */
    if (Object.hasOwn(P.ITEMS, content.item)) {
      /**
       * 🔴 **`drawnOnly` — ask for the DRAWING, never the photograph.**
       *
       * A photographed tool comes back from `picture()` as an `<image>`, and
       * `SVGLoader` finds no paths in one: the hand would fall back to a bare
       * sleeve and the tool would vanish out of your grip. ⚠️ **Putting the
       * bitmap in the hand is attempt ② at the top of this file — the one that
       * turned dev and staging completely black.** The pack shows the
       * photograph; the hand keeps the triangles until that is done on purpose.
       */
      built = fromIcon(P.picture(content, 0, 0, ICON, { drawnOnly: true }));
    } else {
      /* Everything else that can be held is a block you can place. */
      const placed = itemById(itemIdForKey(content.item) ?? -1)?.placesBlock;
      /* 🧱 The real tiles first; flat colours only where there is no plan. */
      built = placed === undefined ? null : (fromBlockTiles(placed) ?? fromBlock(placed));
    }
  } catch {
    /* 🔴 Deliberately swallowed. A drawing that will not build is a bare hand,
       never a dead frame — see the note at the top of this file. */
    built = null;
  }

  cache.set(key, built);
  return built;
}
